"use client"

import { Button } from "@/components/atoms"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { HeartFilledIcon, HeartIcon } from "@/icons"
import { useAppContext } from "@/lib/context/app-context"
import { addWishlistItem, removeWishlistItem } from "@/lib/data/wishlist"
import { umamiTrackEvent } from "@/lib/utils/umami"
import { Wishlist } from "@/types/wishlist"
import { HttpTypes } from "@medusajs/types"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"

export const WishlistButton = ({
  productId,
  wishlist,
  user,
  product,
  skin = "default",
  className,
}: {
  productId: string
  wishlist?: Wishlist[]
  user?: HttpTypes.StoreCustomer | null
  product: HttpTypes.StoreProduct
  skin?: "default" | "barebones"
  className?: string
}) => {
  const [isWishlistAdding, setIsWishlistAdding] = useState(false)
  const [isWishlisted, setIsWishlisted] = useState(
    wishlist?.[0]?.products?.some((item) => item.id === productId)
  )
  const appContext = useAppContext()
  const router = useRouter()
  const variant = product?.variants?.[0]
  const isBarebones = skin === "barebones"

  useEffect(() => {
    setIsWishlisted(
      wishlist?.[0]?.products?.some((item) => item.id === productId)
    )
  }, [wishlist, productId])

  if (!user) {
    if (!isBarebones) return null
    return (
      <LocalizedClientLink
        href="/user"
        className={`bb-wishlist-btn ${className || ""}`}
        aria-label="Add to wishlist"
        onClick={(e) => e.stopPropagation()}
      >
        <HeartIcon size={18} color="#2b2118" />
      </LocalizedClientLink>
    )
  }

  const handleAddToWishlist = async () => {
    try {
      setIsWishlistAdding(true)
      setIsWishlisted(true)
      await addWishlistItem({
        reference_id: productId,
        reference: "product",
      })
      if (variant) {
        appContext.analytics?.("add_to_wishlist", {
          currency: variant?.calculated_price?.currency_code,
          value: variant?.calculated_price?.calculated_amount,
          items: [
            {
              item_id: variant?.sku,
              item_name: product.title,
              price: variant?.calculated_price?.original_amount,
            },
          ],
        })
        umamiTrackEvent("add_to_wishlist", appContext, {
          currency: variant?.calculated_price?.currency_code,
          value: variant?.calculated_price?.calculated_amount,
          items: [
            {
              item_id: variant?.sku,
              item_name: product.title,
              price: variant?.calculated_price?.original_amount,
            },
          ],
        })
      }
      router.refresh()
    } catch (error) {
      console.error(error)
      setIsWishlisted(false)
    } finally {
      setIsWishlistAdding(false)
    }
  }

  const handleRemoveFromWishlist = async () => {
    try {
      setIsWishlistAdding(true)
      setIsWishlisted(false)
      await removeWishlistItem({
        wishlist_id: wishlist?.[0].id!,
        product_id: productId,
      })
      router.refresh()
    } catch (error) {
      console.error(error)
      setIsWishlisted(true)
    } finally {
      setIsWishlistAdding(false)
    }
  }

  if (isBarebones) {
    return (
      <button
        type="button"
        className={`bb-wishlist-btn ${
          isWishlisted ? "bb-wishlist-btn--active" : ""
        } ${isWishlistAdding ? "bb-wishlist-btn--busy" : ""} ${className || ""}`}
        aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
        aria-pressed={isWishlisted}
        disabled={isWishlistAdding}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (isWishlisted) void handleRemoveFromWishlist()
          else void handleAddToWishlist()
        }}
      >
        {isWishlisted ? (
          <HeartFilledIcon size={18} color="#8a3a2a" />
        ) : (
          <HeartIcon size={18} color="#2b2118" />
        )}
      </button>
    )
  }

  return (
    <Button
      onClick={
        isWishlisted
          ? () => handleRemoveFromWishlist()
          : () => handleAddToWishlist()
      }
      variant="tonal"
      className={`w-10 h-10 p-0 flex items-center justify-center ${className || ""}`}
      loading={isWishlistAdding}
      disabled={isWishlistAdding}
    >
      {isWishlisted ? (
        <HeartFilledIcon size={20} color="#ef4444" />
      ) : (
        <HeartIcon size={20} />
      )}
    </Button>
  )
}
